/**
 * As contas do gráfico — tudo o que `Chart.tsx` decide e que dá para provar
 * sem navegador.
 *
 * O componente só desenha. Qual cor cada série veste, o que vira "Outros" na
 * pizza, quanto é cada fatia em porcentagem e em que ordem as barras da
 * corrida ficam em cada quadro mora aqui, com teste ao lado — porque são as
 * perguntas cuja resposta errada desenha bonito.
 */

/**
 * As cores das séries, na ordem em que são distribuídas — **nunca em ciclo**.
 *
 * São as cores de FASE do `theme.css`, e não uma paleta nova: luminosidade
 * igual nas dez, já medidas a 3:1 no teste de contraste. O que foi escolhido
 * aqui é **quais seis e em que ordem**, e a escolha foi medida com o
 * validador de paleta em 28/09/2026 (simulação de protanopia e deuteranopia,
 * ΔE em OKLab ×100, par vizinho):
 *
 * - claro: pior par vizinho **11,3** para daltônico e **20,2** para visão
 *   normal; escuro: **9,7** e **17,6**. O alvo é 8 e 15.
 * - `verde` e `vermelho` ficaram de fora de propósito: lidas ao lado de um
 *   número, elas anunciam "bom" e "ruim", e são as cores de `sucesso` e
 *   `erro`. Com elas cabiam oito séries; sem elas, seis.
 * - `ciano` ficou de fora porque não se separa do `azul` e do `turquesa` a
 *   olho nu (ΔE abaixo de 15 com visão normal).
 *
 * **O que o validador ainda reprova, e fica assim porque são os tokens da
 * casa:** o `turquesa` tem saturação abaixo do piso (0,10 contra ~0,11 — lê
 * um pouco acinzentado), e no escuro as seis estão em L 0,72, acima da faixa
 * que o validador recomenda. Nenhuma das duas cai no contraste.
 *
 * `laranja` abre a lista porque é o matiz da marca. Ele não é o
 * `--cor-primaria`: aquele mede 2,3:1 sobre o cartão branco, abaixo do piso
 * de 3:1 de objeto gráfico.
 */
export const SERIES_COLORS = [
  'fase-laranja',
  'fase-azul',
  'fase-rosa',
  'fase-amarelo',
  'fase-roxo',
  'fase-turquesa',
] as const;

/** A cor do que não é uma série de verdade — o "Outros" da pizza. */
export const OTHERS_COLOR = 'fase-cinza';

export const MAX_SERIES = SERIES_COLORS.length;

/**
 * As cores de ESTADO — fora da distribuição automática de propósito (ver
 * `SERIES_COLORS`), e por isso só entram quando a série as declara.
 *
 * Existem para o gráfico em que a cor É o significado: "estourado" em
 * vermelho, "concluído" em verde. Ali a leitura "bom e ruim" que tirou as duas
 * da paleta é exatamente a que se quer.
 */
export const STATE_COLORS = ['fase-vermelho', 'fase-verde'] as const;

export type ChartColor =
  | (typeof SERIES_COLORS)[number]
  | (typeof STATE_COLORS)[number]
  | typeof OTHERS_COLOR;

/** A cor como o SVG a lê: a variável do token, que resolve claro e escuro. */
export function colorVar(color: ChartColor): string {
  return `var(--cor-${color})`;
}

/**
 * A cor da série de índice `index`.
 *
 * **Uma sétima série não ganha cor: quebra.** Gerar um matiz novo, ou dar a
 * volta na lista, faria duas séries diferentes vestirem a mesma cor — e a
 * figura mentiria em silêncio. Sete séries num gráfico só é sinal de que ele
 * precisa virar dois, ou de que as menores cabem num "Outros".
 */
export function seriesColor(index: number): ChartColor {
  const color = SERIES_COLORS[index];
  if (color === undefined) {
    throw new RangeError(
      `O gráfico tem cor para ${MAX_SERIES} séries, e recebeu a de índice ${index}. ` +
        'Divida em dois gráficos ou junte as menores.',
    );
  }
  return color;
}

/**
 * As cores das séries de um gráfico: as declaradas, ou a distribuição pela
 * ordem.
 *
 * **Ou todas declaram, ou nenhuma.** Misturar faria a série sem cor receber a
 * do índice dela — que pode ser justamente a que outra declarou —, e duas
 * séries vestiriam a mesma cor em silêncio. Pelo mesmo motivo, duas séries que
 * declaram a mesma cor quebram.
 */
export function seriesColors(series: readonly { color?: ChartColor }[]): ChartColor[] {
  const declared = series.filter((s) => s.color !== undefined).length;
  if (declared === 0) return series.map((_, i) => seriesColor(i));
  if (declared !== series.length) {
    throw new Error(
      'Ou todas as séries declaram a cor, ou nenhuma: misturar deixa duas séries com a mesma cor.',
    );
  }
  const colors = series.map((s) => s.color!);
  if (new Set(colors).size !== colors.length) {
    throw new Error('Duas séries declararam a mesma cor.');
  }
  return colors;
}

/** Uma série declarada por quem monta o gráfico. */
export interface SeriesSpec<T> {
  /** Identidade da série — vira a chave da coluna que o Recharts lê. Única. */
  key: string;
  label: string;
  /**
   * A cor, quando ela carrega significado — o estado que a série conta.
   * Ausente, a série recebe a da ordem. Ver `seriesColors`.
   */
  color?: ChartColor;
  /** O valor daquela categoria, ou `null` quando não foi medido — nunca zero inventado. */
  value: (item: T) => number | null;
}

/** Uma linha do que o Recharts desenha: a categoria e um número por série. */
export interface CartesianRow<T> {
  readonly category: string;
  readonly item: T;
  readonly values: Readonly<Record<string, number | null>>;
}

/**
 * Os dados na forma de linhas, com as chaves das séries conferidas.
 *
 * Chave repetida quebra: duas séries com a mesma chave desenham uma por cima
 * da outra e a legenda mostra as duas, sem nada avisando.
 */
export function cartesianRows<T>(
  items: readonly T[],
  category: (item: T) => string,
  series: readonly SeriesSpec<T>[],
): CartesianRow<T>[] {
  const keys = new Set<string>();
  for (const s of series) {
    if (keys.has(s.key)) throw new Error(`Duas séries com a chave "${s.key}".`);
    keys.add(s.key);
  }
  // confere o teto de cores antes de desenhar, e não no meio do desenho
  if (series.length > 0) seriesColor(series.length - 1);

  return items.map((item) => ({
    category: category(item),
    item,
    values: Object.fromEntries(series.map((s) => [s.key, s.value(item)])),
  }));
}

/** Uma fatia da pizza, já com a cor e a porcentagem. */
export interface Slice<T> {
  readonly label: string;
  readonly value: number;
  /** Em décimos de ponto percentual — `125` é 12,5%. A soma das fatias é 1000. */
  readonly shareTenths: number;
  readonly color: ChartColor;
  /** Os registros que a fatia soma. Um, ou vários quando ela é "Outros". */
  readonly items: readonly T[];
  readonly others: boolean;
}

export type PieResult<T> =
  | { readonly kind: 'fatias'; readonly slices: Slice<T>[]; readonly total: number }
  | { readonly kind: 'vazio' }
  /** Pizza não desenha valor negativo: não existe fatia de área negativa. */
  | { readonly kind: 'negativo'; readonly labels: string[] };

/**
 * As fatias da pizza: as maiores com cor própria, o resto num "Outros".
 *
 * - Valor **zero** sai da figura (fatia sem área) e continua na tabela de
 *   quem a usa, porque a tabela lista `items` e não as fatias.
 * - Valor **negativo** recusa a pizza inteira. Descartá-lo mudaria a
 *   porcentagem de todas as outras fatias sem ninguém ver.
 * - O "Outros" só nasce quando junta **duas ou mais**: uma fatia sozinha
 *   chamada "Outros" esconde o nome que ela tinha.
 */
export function pieSlices<T>(
  items: readonly T[],
  label: (item: T) => string,
  value: (item: T) => number,
  maxSlices: number = MAX_SERIES,
): PieResult<T> {
  if (maxSlices < 2 || maxSlices > MAX_SERIES) {
    throw new RangeError(`maxSlices vai de 2 a ${MAX_SERIES}; veio ${maxSlices}.`);
  }
  const negatives = items.filter((i) => value(i) < 0).map(label);
  if (negatives.length > 0) return { kind: 'negativo', labels: negatives };

  const positive = items
    .filter((i) => value(i) > 0)
    .map((i) => ({ label: label(i), value: value(i), items: [i] }))
    // maior primeiro; empate pelo nome, para a ordem não depender da consulta
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, 'pt-BR'));
  if (positive.length === 0) return { kind: 'vazio' };

  const fits = positive.length <= maxSlices;
  const own = fits ? positive : positive.slice(0, maxSlices - 1);
  const rest = fits ? [] : positive.slice(maxSlices - 1);

  const groups = [
    ...own.map((g) => ({ ...g, others: false })),
    ...(rest.length > 0
      ? [
          {
            label: 'Outros',
            value: rest.reduce((s, g) => s + g.value, 0),
            items: rest.flatMap((g) => g.items),
            others: true,
          },
        ]
      : []),
  ];
  const total = groups.reduce((s, g) => s + g.value, 0);
  const tenths = largestRemainder(
    groups.map((g) => g.value),
    1000,
  );

  return {
    kind: 'fatias',
    total,
    slices: groups.map((g, i) => ({
      ...g,
      shareTenths: tenths[i] ?? 0,
      color: g.others ? OTHERS_COLOR : seriesColor(i),
    })),
  };
}

/**
 * Reparte `whole` na proporção de `values` em inteiros que somam `whole`.
 *
 * É o método do maior resto: arredondar cada fatia sozinha dá 33,3 + 33,3 +
 * 33,3 = 99,9%, e a tabela ao lado da pizza passa a somar um número que
 * nenhuma conta explica.
 */
export function largestRemainder(values: readonly number[], whole: number): number[] {
  const total = values.reduce((s, v) => s + v, 0);
  if (total <= 0) return values.map(() => 0);
  const exact = values.map((v) => (v / total) * whole);
  const floors = exact.map(Math.floor);
  let missing = whole - floors.reduce((s, v) => s + v, 0);
  const order = exact
    .map((v, i) => ({ i, rest: v - Math.floor(v) }))
    .sort((a, b) => b.rest - a.rest || a.i - b.i);
  for (const { i } of order) {
    if (missing <= 0) break;
    floors[i] = (floors[i] ?? 0) + 1;
    missing--;
  }
  return floors;
}

/**
 * A participação de cada REGISTRO, na mesma conta das fatias.
 *
 * A tabela ao lado da pizza lista registro, e a legenda lista fatia. Arredondar
 * cada registro sozinho fazia a mesma cidade dizer 19,3% na legenda e 19,4% na
 * tabela — medido no navegador em 28/09. Então o registro que é fatia sozinho
 * herda a porcentagem dela, e os que o "Outros" junta repartem a dele pelo
 * mesmo maior resto. As duas colunas somam o mesmo 100,0%.
 */
export function itemShares<T>(
  slices: readonly Slice<T>[],
  value: (item: T) => number,
): Map<T, number> {
  const shares = new Map<T, number>();
  for (const s of slices) {
    const parts = largestRemainder(s.items.map(value), s.shareTenths);
    s.items.forEach((item, i) => shares.set(item, parts[i] ?? 0));
  }
  return shares;
}

/** "12,5%" a partir dos décimos. */
export function formatShare(tenths: number): string {
  return `${(tenths / 10).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/** Uma barra da corrida num quadro. */
export interface RaceBar {
  readonly entity: string;
  readonly value: number;
  /** 1 é o primeiro. */
  readonly rank: number;
}

export interface RaceFrame {
  readonly frame: string;
  readonly bars: RaceBar[];
}

/**
 * Os quadros da corrida, na ordem em que os quadros aparecem nos dados.
 *
 * **A ordem do tempo é de quem chama.** O componente não sabe se o quadro é
 * "2026-09", "set/26" ou "3º trimestre", e ordenar texto poria "out/26" antes
 * de "set/26". Então os dados chegam já em ordem, e a primeira aparição de
 * cada quadro é a posição dele.
 *
 * Quem não tem linha num quadro **não aparece nele** — não é carregado do
 * quadro anterior nem ganha zero. Se a série é acumulada, quem acumula é a
 * consulta, que sabe o que o número quer dizer.
 */
export function raceFrames<T>(
  items: readonly T[],
  frame: (item: T) => string,
  entity: (item: T) => string,
  value: (item: T) => number,
  top: number,
): RaceFrame[] {
  const byFrame = new Map<string, Map<string, number>>();
  for (const item of items) {
    const f = frame(item);
    const entities = byFrame.get(f) ?? new Map<string, number>();
    const e = entity(item);
    // duas linhas da mesma entidade no mesmo quadro somam — é a mesma barra
    entities.set(e, (entities.get(e) ?? 0) + value(item));
    byFrame.set(f, entities);
  }
  return [...byFrame].map(([f, entities]) => ({
    frame: f,
    bars: [...entities]
      .map(([e, v]) => ({ entity: e, value: v }))
      .sort((a, b) => b.value - a.value || a.entity.localeCompare(b.entity, 'pt-BR'))
      .slice(0, top)
      .map((b, i) => ({ ...b, rank: i + 1 })),
  }));
}

/**
 * A cor de cada entidade da corrida — **presa à entidade, nunca à posição**.
 *
 * Uma cor por posição faria a barra trocar de cor ao ultrapassar a vizinha, e
 * o olho segue a cor, não o nome. Com até seis entidades na corrida inteira
 * cada uma tem a sua; com mais, todas vestem a mesma e quem identifica é o
 * nome escrito na barra — nunca uma cor dada em ciclo.
 */
export function raceColors(frames: readonly RaceFrame[]): Map<string, ChartColor> {
  const entities = [...new Set(frames.flatMap((f) => f.bars.map((b) => b.entity)))].sort(
    (a, b) => a.localeCompare(b, 'pt-BR'),
  );
  const single = entities.length > MAX_SERIES;
  return new Map(entities.map((e, i) => [e, single ? SERIES_COLORS[0] : seriesColor(i)]));
}

/** O formato padrão do número: inteiro ou decimal, à brasileira. */
export function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}
